import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Pencil, Plus, Save, Sparkles, Trash2, WandSparkles } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ListSkeleton } from './ui/skeleton';
import { Textarea } from './ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import ModuleShell from './callcopilot/ModuleShell';
import { EmptyState } from './callcopilot/EmptyState';

const ROLES = [['seller', 'Vendedor'], ['buyer', 'Comprador'], ['manager', 'Gestor'], ['leader', 'Líder'], ['report', 'Liderado']];
const OBJECTIVES = { seller: [['qualification', 'Qualificação'], ['discovery', 'Descoberta de necessidades'], ['negotiation', 'Negociação'], ['renewal', 'Renovação'], ['expansion', 'Expansão de conta']], buyer: [['evaluation', 'Avaliação de fornecedor'], ['procurement', 'Compras e contratação'], ['negotiation', 'Negociação']], manager: [['meeting', 'Reunião'], ['planning', 'Planejamento'], ['decision', 'Tomada de decisão']], leader: [['one_on_one', '1:1 / Condução'], ['feedback', 'Feedback'], ['development', 'Desenvolvimento']], report: [['execution', 'Execução'], ['planning', 'Planejamento'], ['clarification', 'Alinhamento de execução']] };
const blank = { name: '', objective_name: '', description: '', role: 'seller', objective: 'negotiation', guidance: '' };
const HOW_IT_WORKS = [
  'O objetivo é a intenção permanente do papel. O escopo da reunião é o contexto temporário.',
  'Antes de gravar, a IA combina os dois e cria tópicos, gatilhos, perguntas e ações específicos daquela conversa.',
  'Você escolhe o objetivo cadastrado na tela de iniciar conversa.',
];

export default function CallCopilotPlaybooks({ api }) {
  const [playbooks, setPlaybooks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const res = await api.get('/call-copilot/playbooks'); setPlaybooks((res.data?.playbooks || []).filter((item) => item.license_id)); } catch { toast.error('Não foi possível carregar os objetivos'); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const objectiveOptions = useMemo(() => OBJECTIVES[form.role] || [], [form.role]);
  const startNew = () => { setEditing('new'); setForm(blank); };
  const edit = (item) => { setEditing(item.id); setForm({ name: item.name || '', objective_name: item.objective_name || item.name || '', description: item.description || '', role: item.role || 'seller', objective: item.objective || 'negotiation', guidance: item.guidance || '' }); };
  const save = async (event) => { event.preventDefault(); if (!form.objective_name.trim() || !form.guidance.trim()) { toast.error('Informe o nome e a orientação do objetivo'); return; } const payload = { ...form, name: form.name.trim() || form.objective_name.trim(), topics: [] }; try { if (editing === 'new') await api.post('/call-copilot/playbooks', payload); else await api.put(`/call-copilot/playbooks/${editing}`, payload); toast.success('Objetivo salvo'); setEditing(null); load(); } catch (error) { toast.error(error?.response?.data?.detail || 'Erro ao salvar objetivo'); } };
  const remove = async (item) => { if (!window.confirm(`Excluir o objetivo “${item.objective_name || item.name}”?`)) return; try { await api.delete(`/call-copilot/playbooks/${item.id}`); toast.success('Objetivo excluído'); load(); } catch { toast.error('Erro ao excluir objetivo'); } };

  return (
    <ModuleShell
      pageId="call-playbooks"
      title="Objetivos e playbooks"
      subtitle="Cadastre o objetivo e a orientação permanente. A IA cria os tópicos e gatilhos a partir deles e do escopo da conversa."
      icon={BookOpen}
      actions={[{ id: 'novo-objetivo', label: 'Novo objetivo', icon: Plus, variant: 'emerald', onClick: startNew }]}
    >
      <TooltipProvider delayDuration={200}>
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 space-y-3">
            {loading ? <ListSkeleton items={2} /> : playbooks.length === 0 ? (
              <EmptyState
                icon={BookOpen}
                title="Nenhum objetivo personalizado cadastrado"
                description="Defina a intenção permanente de cada papel e deixe a IA gerar tópicos, gatilhos e perguntas em tempo real."
                action={{ label: 'Cadastrar primeiro objetivo', onClick: startNew, variant: 'emerald' }}
              />
            ) : playbooks.map((item) => {
              const guidance = item.guidance || '';
              const description = item.description || guidance;
              return (
                <Card key={item.id} className="border-slate-200 bg-white shadow-sm">
                  <CardContent className="flex flex-wrap items-start justify-between gap-4 p-5">
                    <div className="flex min-w-0 gap-3">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-violet-50 text-violet-700"><BookOpen className="h-5 w-5" /></span>
                      <div className="min-w-0">
                        <h2 className="font-semibold text-slate-950">{item.objective_name || item.name}</h2>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <Badge variant="secondary">{ROLES.find(([key]) => key === item.role)?.[1] || item.role}</Badge>
                          <Badge className="border border-violet-200 bg-violet-50 text-violet-700">{OBJECTIVES[item.role]?.find(([key]) => key === item.objective)?.[1] || item.objective}</Badge>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">{description}</p>
                        {guidance && description !== guidance && <p className="mt-2 line-clamp-2 text-xs text-slate-400">Orientação: {guidance}</p>}
                        <p className="mt-3 text-[11px] text-slate-400">Tópicos e gatilhos gerados pela IA a cada conversa</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button type="button" variant="outline" size="icon" title="Editar objetivo" aria-label={`Editar ${item.objective_name || item.name}`} onClick={() => edit(item)}><Pencil size={15} /></Button>
                        </TooltipTrigger>
                        <TooltipContent>Editar</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button type="button" variant="ghost" size="icon" title="Excluir objetivo" aria-label={`Excluir ${item.objective_name || item.name}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" onClick={() => remove(item)}><Trash2 size={15} /></Button>
                        </TooltipTrigger>
                        <TooltipContent>Excluir</TooltipContent>
                      </Tooltip>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <Card className="h-fit border-violet-200 bg-violet-50/60 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><WandSparkles size={17} className="text-violet-700" /> Como funciona</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {HOW_IT_WORKS.map((text) => (
                <p key={text} className="flex gap-2 text-sm leading-6 text-violet-950">
                  <Sparkles size={16} className="mt-1 shrink-0 text-violet-600" />
                  <span>{text}</span>
                </p>
              ))}
            </CardContent>
          </Card>
        </div>
      </TooltipProvider>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Novo objetivo' : 'Editar objetivo'}</DialogTitle>
            <DialogDescription>Obrigatório: nome do objetivo e orientação para a IA.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={save}>
            <div>
              <Label>Nome do objetivo <span className="text-rose-500">*</span></Label>
              <Input value={form.objective_name} onChange={(e) => setForm({ ...form, objective_name: e.target.value })} placeholder="Ex.: Obter a melhor condição sem comprometer os critérios técnicos" />
            </div>
            <div>
              <Label>Nome interno do playbook</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex.: Negociação de renovação" />
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Quando este objetivo deve ser usado" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Papel</Label>
                <select className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={form.role} onChange={(e) => { const role = e.target.value; setForm({ ...form, role, objective: OBJECTIVES[role][0][0] }); }}>{ROLES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
              </div>
              <div>
                <Label>Tipo de objetivo</Label>
                <select className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })}>{objectiveOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>
              </div>
            </div>
            <div>
              <Label>Orientação para a IA <span className="text-rose-500">*</span></Label>
              <Textarea rows={8} value={form.guidance} onChange={(e) => setForm({ ...form, guidance: e.target.value })} placeholder="Descreva o que o copiloto deve priorizar, evitar, perguntar e buscar neste papel." />
              <p className="mt-1 text-xs text-slate-500">Os tópicos e gatilhos serão gerados automaticamente pela IA a cada conversa.</p>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" className="bg-emerald-600 text-white shadow-sm hover:bg-emerald-700"><Save size={15} /> Salvar objetivo</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </ModuleShell>
  );
}
